import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, MapPin } from "lucide-react";

/**
 * Editor untuk daftar area tugas PPDP.
 * Setiap area: { dusun, rw?, rt? }
 *  - rw kosong = seluruh RW di dusun tsb
 *  - rt kosong = seluruh RT di RW tsb
 */
export function AssignedAreasEditor({ value, onChange, dusuns }) {
  const areas = value || [];

  const addEmpty = () => {
    onChange([...areas, { dusun: dusuns[0]?.nama || "", rw: "", rt: "" }]);
  };

  const updateAt = (idx, patch) => {
    onChange(areas.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const removeAt = (idx) => {
    onChange(areas.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      {areas.length === 0 && (
        <div className="rounded-md border border-dashed border-slate-300 p-4 text-center text-xs text-slate-500">
          Belum ada area tugas. Tambahkan minimal satu.
        </div>
      )}
      {areas.map((a, idx) => (
        <AreaRow
          key={idx}
          area={a}
          dusuns={dusuns}
          onChange={(patch) => updateAt(idx, patch)}
          onRemove={() => removeAt(idx)}
          testidPrefix={`area-${idx}`}
        />
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addEmpty}
        disabled={dusuns.length === 0}
        data-testid="add-area-button"
        className="w-full font-semibold"
      >
        <Plus className="h-4 w-4 mr-1" /> Tambah Area
      </Button>
      <p className="text-[11px] text-slate-500 leading-snug">
        Kosongkan RW untuk menugaskan seluruh dusun. Kosongkan RT untuk menugaskan seluruh RW.
      </p>
    </div>
  );
}

function AreaRow({ area, dusuns, onChange, onRemove, testidPrefix }) {
  const [rwOptions, setRwOptions] = useState([]);
  const [rtOptions, setRtOptions] = useState([]);

  // Load distinct RW options when dusun changes
  useEffect(() => {
    if (!area.dusun) { setRwOptions([]); return; }
    let cancelled = false;
    api.get("/wilayah/options", { params: { dusun: area.dusun } }).then(({ data }) => {
      if (!cancelled) setRwOptions(data.rws || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [area.dusun]);

  // Load distinct RT options when (dusun, rw) change
  useEffect(() => {
    if (!area.dusun || !area.rw) { setRtOptions([]); return; }
    let cancelled = false;
    api.get("/wilayah/options", { params: { dusun: area.dusun, rw: area.rw } }).then(({ data }) => {
      if (!cancelled) setRtOptions(data.rts || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [area.dusun, area.rw]);

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700">
          <MapPin className="h-3 w-3" /> Area
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="text-slate-400 hover:text-red-600"
          aria-label="Hapus area"
          data-testid={`${testidPrefix}-remove`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {/* Dusun */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Dusun *</Label>
          <Select
            value={area.dusun || ""}
            onValueChange={(v) => onChange({ dusun: v, rw: "", rt: "" })}
          >
            <SelectTrigger className="h-10" data-testid={`${testidPrefix}-dusun`}>
              <SelectValue placeholder="Pilih" />
            </SelectTrigger>
            <SelectContent>
              {dusuns.map((d) => (
                <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* RW */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">RW</Label>
          {rwOptions.length > 0 ? (
            <Select
              value={area.rw || "__all__"}
              onValueChange={(v) => onChange({ rw: v === "__all__" ? "" : v, rt: "" })}
            >
              <SelectTrigger className="h-10" data-testid={`${testidPrefix}-rw`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Semua RW</SelectItem>
                {rwOptions.map((rw) => (
                  <SelectItem key={rw} value={rw}>RW {rw}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={area.rw || ""}
              onChange={(e) => onChange({ rw: e.target.value, rt: "" })}
              placeholder="Semua RW"
              className="h-10 font-mono"
              data-testid={`${testidPrefix}-rw-input`}
            />
          )}
        </div>

        {/* RT */}
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">RT</Label>
          {rtOptions.length > 0 ? (
            <Select
              value={area.rt || "__all__"}
              onValueChange={(v) => onChange({ rt: v === "__all__" ? "" : v })}
              disabled={!area.rw}
            >
              <SelectTrigger className="h-10" data-testid={`${testidPrefix}-rt`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Semua RT</SelectItem>
                {rtOptions.map((rt) => (
                  <SelectItem key={rt} value={rt}>RT {rt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={area.rt || ""}
              onChange={(e) => onChange({ rt: e.target.value })}
              placeholder={area.rw ? "Semua RT" : "Pilih RW dulu"}
              disabled={!area.rw}
              className="h-10 font-mono"
              data-testid={`${testidPrefix}-rt-input`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** Compact badge list of areas. Useful in dashboards/sidebars. */
export function AssignedAreasBadges({ areas, className = "" }) {
  if (!areas?.length) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {areas.map((a, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700"
        >
          <MapPin className="h-3 w-3 opacity-60" />
          {areaLabel(a)}
        </span>
      ))}
    </div>
  );
}

export function areaLabel(a) {
  if (!a) return "";
  let s = a.dusun || "";
  if (a.rw) s += ` · RW ${a.rw}`;
  if (a.rt) s += ` / RT ${a.rt}`;
  return s;
}
